import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { fmtMoney, formatApiErrorDetail } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { CreditCard, DeviceMobile, Wallet, CheckCircle } from "@phosphor-icons/react";

const METHODS = [
  { id: "card", label: "Carte bancaire", sub: "Visa · Mastercard", icon: <CreditCard weight="duotone" size={26} />, color: "text-slate-700" },
  { id: "orange", label: "Orange Money", sub: "Mobile Money", icon: <DeviceMobile weight="duotone" size={26} />, color: "text-orange-600" },
  { id: "wave", label: "Wave", sub: "Mobile Money", icon: <DeviceMobile weight="duotone" size={26} />, color: "text-sky-600" },
  { id: "paypal", label: "PayPal", sub: "Portefeuille en ligne", icon: <Wallet weight="duotone" size={26} />, color: "text-blue-700" },
];

// Card = Stripe (réel). Orange/Wave/PayPal = SIMULÉS pour le MVP (aucune passerelle marchande réelle connectée).
export const PaymentDialog = ({ open, onOpenChange, booking }) => {
  const navigate = useNavigate();
  const [method, setMethod] = useState("card");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);

  const pay = async () => {
    if (!booking) return;
    setLoading(true);
    try {
      if (method === "card") {
        const { data } = await api.post("/payments/checkout", { booking_id: booking.id, origin_url: window.location.origin });
        window.location.href = data.checkout_url;
        return;
      }
      if (method === "paypal") {
        const { data } = await api.post("/payments/paypal/create", { booking_id: booking.id, origin_url: window.location.origin });
        if (data.approval_url) { window.location.href = data.approval_url; return; }
        toast.success("Paiement PayPal confirmé (démo) !");
        onOpenChange(false); navigate("/bookings"); return;
      }
      if ((method === "orange" || method === "wave") && !phone.trim()) {
        toast.error("Entrez votre numéro Mobile Money."); setLoading(false); return;
      }
      await api.post("/payments/mobile-money", { booking_id: booking.id, provider: method, phone });
      toast.success("Paiement confirmé (simulé) !");
      onOpenChange(false);
      navigate("/bookings");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setLoading(false);
    }
  };

  const isMobile = method === "orange" || method === "wave";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-display text-xl">Paiement</DialogTitle>
          <DialogDescription>Choisissez votre moyen de paiement pour finaliser la réservation.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-between bg-stone-50 rounded-xl p-3 border border-stone-200">
          <span className="text-sm text-stone-500">{booking?.property_title}</span>
          <span className="font-display text-lg font-bold text-orange-800">{fmtMoney(booking?.amount)}</span>
        </div>

        <div className="grid grid-cols-2 gap-2 mt-1">
          {METHODS.map((m) => (
            <button key={m.id} data-testid={`pay-method-${m.id}`} onClick={() => setMethod(m.id)}
              className={`flex items-center gap-2 rounded-xl border-2 p-3 text-left transition-colors ${method === m.id ? "border-orange-800 bg-orange-50" : "border-stone-200"}`}>
              <span className={m.color}>{m.icon}</span>
              <span>
                <span className="block text-sm font-semibold text-stone-900">{m.label}</span>
                <span className="block text-[11px] text-stone-500">{m.sub}</span>
              </span>
            </button>
          ))}
        </div>

        {isMobile && (
          <div className="mt-1">
            <Label>Numéro {method === "orange" ? "Orange Money" : "Wave"}</Label>
            <Input data-testid="pay-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+221 77 000 00 00" className="mt-1" />
          </div>
        )}

        {method !== "card" && (
          <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
            Mode démo : {METHODS.find((m) => m.id === method).label} est simulé (aucun débit réel).
          </p>
        )}
        <p className="text-[11px] text-stone-500">Le paiement est reçu par Lomar Properties (intermédiaire sécurisé anti-fraude) puis reversé au propriétaire.</p>

        <Button data-testid="pay-confirm-btn" onClick={pay} disabled={loading} className="w-full rounded-full bg-orange-800 hover:bg-orange-900 text-white h-11">
          {loading ? "Traitement…" : method === "card" ? "Payer par carte" : <><CheckCircle weight="fill" className="mr-1" /> Confirmer le paiement</>}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
